
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Star, User, MessageCircle } from 'lucide-react';
import { collection, query, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';
import { USERS_COLLECTION } from '../../config/constants';
import { useAppContext } from '../../App.jsx';

const REVIEWS_PER_PAGE = 12;

const ReviewItem = ({ review }) => (
    <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-3">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center">
                {/* CONTAINER SQUIRCLE PARA AVALIADOR */}
                <div className="w-10 h-10 bg-blue-50 rounded-xl mr-3 flex items-center justify-center border border-blue-100 shadow-inner">
                    <User size={18} className="text-blue-500" />
                </div>
                <div>
                    <span className="text-sm font-bold text-gray-800 block">{review.raterName || 'Usuário'}</span>
                    <span className="text-[10px] text-gray-400 uppercase font-black tracking-tighter">
                        {review.createdAt?.toDate ? review.createdAt.toDate().toLocaleDateString('pt-BR') : 'Recente'}
                    </span>
                </div>
            </div>
            <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-lg border border-yellow-100">
                <Star size={12} className="text-yellow-500 fill-current mr-1" />
                <span className="text-xs font-black text-yellow-700">{review.rating?.toFixed(1)}</span>
            </div>
        </div>

        {review.comment ? (
            <p className="text-sm text-gray-600 leading-relaxed italic">"{review.comment}"</p>
        ) : (
            <p className="text-xs text-gray-400 italic">O usuário não deixou um comentário.</p>
        )}

        {review.tags && review.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
                {review.tags.map((tag, idx) => (
                    <span key={idx} className="text-[9px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold uppercase">
                        {tag}
                    </span>
                ))}
            </div>
        )}
    </div>
);

const AllReviewsModal = ({ serviId, serviName, onClose }) => {
    const { db } = useAppContext();
    const [reviews, setReviews] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const scrollContainerRef = useRef(null);

    const fetchReviews = useCallback(async (loadMore = false) => {
        if (!db || !serviId) return;
        if (loadMore && !lastVisible) return;

        if (loadMore) setIsLoadingMore(true);
        else setIsLoading(true);

        try {
            let q = query(
                collection(db, USERS_COLLECTION, serviId, 'ratings'),
                orderBy('createdAt', 'desc'),
                limit(REVIEWS_PER_PAGE)
            );

            if (loadMore && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const querySnapshot = await getDocs(q);
            const newReviews = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (loadMore) {
                setReviews(prev => [...prev, ...newReviews]);
            } else {
                setReviews(newReviews);
            }

            if (querySnapshot.docs.length > 0) {
                setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
            }
            setHasMore(newReviews.length === REVIEWS_PER_PAGE);

        } catch (error) {
            console.error("Erro ao buscar avaliações:", error);
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [db, serviId, lastVisible]);

    useEffect(() => {
        fetchReviews();
    }, [serviId]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop < clientHeight * 1.5 && hasMore && !isLoadingMore) {
            fetchReviews(true);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4 transition-all" onClick={onClose}>
            <div
                className="bg-gray-50 rounded-[2.5rem] w-full max-w-lg overflow-hidden shadow-2xl animate-slide-up flex flex-col"
                style={{ maxHeight: '85vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="p-6 bg-white border-b flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-gray-800 leading-tight">Avaliações</h2>
                        <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{serviName}</p>
                    </div>
                    <button onClick={onClose} className="p-3 hover:bg-gray-100 rounded-full transition-colors bg-gray-50 border border-gray-100">
                        <X size={20} className="text-gray-600" />
                    </button>
                </header>

                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="p-6 overflow-y-auto flex-grow bg-gray-50"
                >
                    {isLoading && reviews.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                            <p className="text-gray-500 font-bold text-sm uppercase tracking-widest">Carregando depoimentos...</p>
                        </div>
                    ) : reviews.length > 0 ? (
                        <>
                            <div className="space-y-1">
                                {reviews.map(review => <ReviewItem key={review.id} review={review} />)}
                            </div>

                            {isLoadingMore && (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
                                </div>
                            )}

                            {!hasMore && (
                                <p className="text-center text-gray-400 text-[10px] font-black uppercase tracking-widest py-8">
                                    Você chegou ao fim das avaliações
                                </p>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-20">
                            <MessageCircle className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                            <p className="text-gray-500 font-bold">Nenhuma avaliação encontrada.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AllReviewsModal;
